import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductUSA } from "@/types/api/api-types";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { volumesOptions } from "@/constants/options/volumes.option";
import { handleOpenDocument } from "@/lib/file.util";

export const getProductUsaColumns = (
  t: TFunction<"boCatalog">,
  onEdit: (product: ProductUSA) => void,
  onDelete: (product: ProductUSA) => void,
  canManageProducts: boolean,
  wineColorLabels: Record<WineColorEnum, string>,
): ColumnDef<ProductUSA>[] => [
  {
    accessorKey: "description",
    header: t("productsUsaTable.columns.description"),
    cell: (info) => (
      <span className="text-base font-medium text-gray-900">{info.row.original.description}</span>
    ),
  },
  {
    accessorKey: "descriptionPelican",
    header: t("productsUsaTable.columns.descriptionPelican"),
    cell: (info) => (
      <span className="text-sm text-gray-600">{info.row.original.descriptionPelican}</span>
    ),
  },
  {
    accessorKey: "countryOfOrigin",
    header: t("productsUsaTable.columns.countryOfOrigin"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">{info.row.original.countryOfOrigin}</span>
    ),
  },
  {
    accessorKey: "harmonizedCode",
    header: t("productsUsaTable.columns.harmonizedCode"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">{info.row.original.harmonizedCode}</span>
    ),
  },
  {
    accessorKey: "color",
    header: t("productsUsaTable.columns.color"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">
        {wineColorLabels[info.row.original.color as WineColorEnum] || info.row.original.color}
      </span>
    ),
  },
  {
    accessorKey: "volume",
    header: t("productsUsaTable.columns.volume"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">
        {volumesOptions.find((v) => v.value === info.row.original.volume)?.label ||
          `${info.row.original.volume} L`}
      </span>
    ),
  },
  {
    accessorKey: "alcoholPercentage",
    header: t("productsUsaTable.columns.alcoholPercentage"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">{info.row.original.alcoholPercentage}%</span>
    ),
  },
  {
    accessorKey: "ttbNumber",
    header: t("productsUsaTable.columns.ttbNumber"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">{info.row.original.ttbNumber ?? "-"}</span>
    ),
  },
  {
    id: "importer",
    header: t("productsUsaTable.columns.importer"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">{info.row.original.importer?.name ?? "-"}</span>
    ),
  },
  {
    id: "client",
    header: t("productsUsaTable.columns.client"),
    enableSorting: false,
    cell: (info) => (
      <span className="text-sm text-gray-600">{info.row.original.client?.businessName ?? "-"}</span>
    ),
  },
  {
    id: "cola",
    header: t("productsUsaTable.columns.cola"),
    enableSorting: false,
    cell: (info) => (
      <div className="flex flex-row gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleOpenDocument(info.row.original.cola.path)}
        >
          <Eye className="h-4 w-4 text-blue-500" />
        </Button>
      </div>
    ),
  },
  {
    id: "actions",
    header: "",
    enableSorting: false,
    cell: (info) => (
      <div className="flex flex-row gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(info.row.original)}
          disabled={!canManageProducts}
        >
          <Pencil className="h-4 w-4 text-gray-500" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(info.row.original)}
          disabled={!canManageProducts}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  },
];
